import { cn, formatCompactNumber } from '@/lib/utils'
import { TokenBadge } from './TokenBadge'
import type { TokenUsage, TokenInteraction, ModelCostBreakdown } from '@/types/chatbot'
import { Cpu, Bot, ChevronDown, ChevronRight, Loader2, ArrowDownToLine, ArrowUpFromLine, Zap, Clock, DollarSign } from 'lucide-react'
import { useState } from 'react'

export interface SessionTokenUsageProps {
  /** Token usage data for the session */
  tokenUsage?: TokenUsage
  /** Display variant */
  variant?: 'compact' | 'expanded'
  /** Loading state */
  isLoading?: boolean
  /** Error message */
  error?: string
  /** Additional CSS classes */
  className?: string
}

interface ModelRowProps {
  modelName: string
  inputTokens: number
  outputTokens: number
  interactionCount: number
  cost?: ModelCostBreakdown | null
}

function ModelRow({ modelName, inputTokens, outputTokens, interactionCount, cost }: ModelRowProps) {
  const total = inputTokens + outputTokens
  if (total === 0) return null

  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Cpu size={14} className="text-purple-500" />
        <span className="truncate max-w-[150px]" title={modelName}>{modelName}</span>
        <span className="text-xs text-gray-400">({interactionCount})</span>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <span className="text-gray-500 flex items-center gap-1" title="Input tokens">
          <ArrowDownToLine size={10} className="text-blue-500" />
          {formatCompactNumber(inputTokens)}
        </span>
        <span className="text-gray-500 flex items-center gap-1" title="Output tokens">
          <ArrowUpFromLine size={10} className="text-green-500" />
          {formatCompactNumber(outputTokens)}
        </span>
        <span className="font-medium text-gray-700 min-w-[60px] text-right">
          {formatCompactNumber(total)}
        </span>
        {cost != null && (
          <span className="text-amber-600 min-w-[60px] text-right flex items-center justify-end gap-0.5" title={`Cost: $${cost.totalCost.toFixed(4)}`}>
            <DollarSign size={10} />
            {cost.totalCost.toFixed(2)}
          </span>
        )}
      </div>
    </div>
  )
}

interface AgentRowProps {
  agent: string
  inputTokens: number
  outputTokens: number
  interactionCount: number
}

function AgentRow({ agent, inputTokens, outputTokens, interactionCount }: AgentRowProps) {
  const total = inputTokens + outputTokens
  if (total === 0) return null

  const icon = agent === 'Orchestrator' 
    ? <Cpu size={14} className="text-blue-500" />
    : <Bot size={14} className="text-orange-500" />

  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        {icon}
        <span>{agent}</span>
        <span className="text-xs text-gray-400">({interactionCount})</span>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <span className="text-gray-500 flex items-center gap-1" title="Input tokens">
          <ArrowDownToLine size={10} className="text-blue-500" />
          {formatCompactNumber(inputTokens)}
        </span>
        <span className="text-gray-500 flex items-center gap-1" title="Output tokens">
          <ArrowUpFromLine size={10} className="text-green-500" />
          {formatCompactNumber(outputTokens)}
        </span>
        <span className="font-medium text-gray-700 min-w-[60px] text-right">
          {formatCompactNumber(total)}
        </span>
      </div>
    </div>
  )
}

interface InteractionRowProps {
  interaction: TokenInteraction
  index: number
}

function InteractionRow({ interaction, index }: InteractionRowProps) {
  const agentIcon = interaction.agent === 'Orchestrator'
    ? <Cpu size={12} className="text-blue-500" />
    : <Bot size={12} className="text-orange-500" />

  return (
    <div className="flex items-center justify-between py-1 text-xs border-b border-gray-50 last:border-b-0">
      <div className="flex items-center gap-2 text-gray-500">
        <span className="text-gray-300 w-4 text-right">#{index + 1}</span>
        {agentIcon}
        <span className="truncate max-w-[100px]" title={interaction.modelName}>{interaction.modelName}</span>
        {interaction.toolCalls > 0 && (
          <span className="flex items-center gap-0.5 text-gray-400" title="Tool calls">
            <Zap size={10} />
            {interaction.toolCalls}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-blue-600">{formatCompactNumber(interaction.inputTokens)}</span>
        <span className="text-gray-300">/</span>
        <span className="text-green-600">{formatCompactNumber(interaction.outputTokens)}</span>
      </div>
    </div>
  )
}

/**
 * SessionTokenUsage displays detailed token usage breakdown for a session.
 * Shows model-based and agent-based breakdowns with input/output tokens.
 */
export function SessionTokenUsage({
  tokenUsage,
  variant = 'expanded',
  isLoading = false,
  error,
  className,
}: SessionTokenUsageProps) {
  const [isExpanded, setIsExpanded] = useState(variant === 'expanded')
  const [showInteractions, setShowInteractions] = useState(false)

  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2 text-muted-foreground text-sm', className)}>
        <Loader2 size={14} className="animate-spin" />
        <span>Loading...</span>
      </div>
    )
  }

  if (error) {
    return null // Silently hide on error for compact view
  }

  if (!tokenUsage || tokenUsage.totalTokens === 0) {
    return null // Hide when no data
  }

  const { totalTokens, totalInputTokens, totalOutputTokens, modelBreakdown, agentBreakdown, interactions, estimatedCost, costByModel } = tokenUsage

  // Create cost lookup map for model rows
  const costMap = new Map<string, ModelCostBreakdown>()
  if (costByModel) {
    costByModel.forEach(c => costMap.set(c.modelName, c))
  }

  // Compact variant - just a subtle inline display
  if (variant === 'compact') {
    return (
      <div className={cn('relative', className)}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm',
            'bg-muted/50 hover:bg-muted text-muted-foreground',
            'transition-colors border border-transparent hover:border-border'
          )}
          title={`Total: ${totalTokens.toLocaleString()} (In: ${totalInputTokens.toLocaleString()} / Out: ${totalOutputTokens.toLocaleString()})${estimatedCost != null ? ` • Est. $${estimatedCost.toFixed(2)}` : ''}`}
        >
          <Cpu size={14} />
          <span className="font-medium">{formatCompactNumber(totalTokens)}</span>
          <span className="text-xs opacity-60">tokens</span>
          {estimatedCost != null && (
            <span className="flex items-center gap-0.5 text-amber-600 ml-1">
              <DollarSign size={10} />
              {estimatedCost.toFixed(2)}
            </span>
          )}
          {isExpanded ? (
            <ChevronDown size={12} className="ml-1 opacity-60" />
          ) : (
            <ChevronRight size={12} className="ml-1 opacity-60" />
          )}
        </button>

        {/* Expanded dropdown */}
        {isExpanded && (
          <div className="absolute right-0 top-full mt-2 z-50 min-w-[360px] rounded-lg border bg-popover shadow-lg p-3 space-y-2">
            {/* Summary */}
            <div className="flex items-center justify-between text-sm pb-2 border-b">
              <span className="text-muted-foreground">Total</span>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-blue-600">
                  <ArrowDownToLine size={12} />
                  {formatCompactNumber(totalInputTokens)}
                </span>
                <span className="flex items-center gap-1 text-green-600">
                  <ArrowUpFromLine size={12} />
                  {formatCompactNumber(totalOutputTokens)}
                </span>
                {estimatedCost != null && (
                  <span className="flex items-center gap-1 text-amber-600 font-medium">
                    <DollarSign size={12} />
                    {estimatedCost.toFixed(2)}
                  </span>
                )}
              </div>
            </div>

            {/* Model breakdown */}
            {modelBreakdown && modelBreakdown.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  By Model
                </div>
                {modelBreakdown.map((model) => (
                  <ModelRow
                    key={model.modelName}
                    modelName={model.modelName}
                    inputTokens={model.inputTokens}
                    outputTokens={model.outputTokens}
                    interactionCount={model.interactionCount}
                    cost={costMap.get(model.modelName)}
                  />
                ))}
              </div>
            )}

            {/* Agent breakdown */}
            {agentBreakdown && agentBreakdown.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  By Agent
                </div>
                {agentBreakdown.map((agent) => (
                  <AgentRow
                    key={agent.agent}
                    agent={agent.agent}
                    inputTokens={agent.inputTokens}
                    outputTokens={agent.outputTokens}
                    interactionCount={agent.interactionCount}
                  />
                ))}
              </div>
            )}
            
            {/* Footer with last updated */}
            {tokenUsage.lastUpdated && (
              <div className="pt-2 mt-2 border-t flex justify-between items-center text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock size={10} />
                  {new Date(tokenUsage.lastUpdated).toLocaleTimeString()}
                </span>
                <span>{interactions?.length ?? 0} interactions</span>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Expanded variant (full card)
  return (
    <div className={cn('rounded-lg border bg-card overflow-hidden', className)}>
      {/* Header with total */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'w-full flex items-center justify-between p-3',
          'bg-muted/30 hover:bg-muted/50 transition-colors'
        )}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown size={16} className="text-muted-foreground" />
          ) : (
            <ChevronRight size={16} className="text-muted-foreground" />
          )}
          <span className="font-medium">Token Usage</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs text-blue-600">
            <ArrowDownToLine size={12} />
            {formatCompactNumber(totalInputTokens)}
          </span>
          <span className="flex items-center gap-1 text-xs text-green-600">
            <ArrowUpFromLine size={12} />
            {formatCompactNumber(totalOutputTokens)}
          </span>
          <TokenBadge tokens={totalTokens} showLabel={false} size="sm" />
          {estimatedCost != null && (
            <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
              <DollarSign size={12} />
              {estimatedCost.toFixed(2)}
            </span>
          )}
        </div>
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="p-3 space-y-3">
          {/* Model breakdown */}
          {modelBreakdown && modelBreakdown.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                By Model
              </div>
              {modelBreakdown.map((model) => (
                <ModelRow
                  key={model.modelName}
                  modelName={model.modelName}
                  inputTokens={model.inputTokens}
                  outputTokens={model.outputTokens}
                  interactionCount={model.interactionCount}
                  cost={costMap.get(model.modelName)}
                />
              ))}
            </div>
          )}

          {/* Agent breakdown */}
          {agentBreakdown && agentBreakdown.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                By Agent
              </div>
              {agentBreakdown.map((agent) => (
                <AgentRow
                  key={agent.agent}
                  agent={agent.agent}
                  inputTokens={agent.inputTokens}
                  outputTokens={agent.outputTokens}
                  interactionCount={agent.interactionCount}
                />
              ))}
            </div>
          )}

          {/* Interactions toggle */}
          {interactions && interactions.length > 0 && (
            <div className="pt-2 border-t">
              <button
                onClick={() => setShowInteractions(!showInteractions)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {showInteractions ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <span>Recent interactions ({interactions.length})</span>
              </button>
              
              {showInteractions && (
                <div className="mt-2 max-h-[200px] overflow-y-auto">
                  {interactions.slice(0, 10).map((interaction, index) => (
                    <InteractionRow
                      key={interaction.timestamp + index}
                      interaction={interaction}
                      index={index}
                    />
                  ))}
                  {interactions.length > 10 && (
                    <div className="text-xs text-center text-muted-foreground py-1">
                      +{interactions.length - 10} more
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Footer with last updated */}
          {tokenUsage.lastUpdated && (
            <div className="pt-2 text-xs text-muted-foreground text-right flex items-center justify-end gap-1">
              <Clock size={10} />
              Last updated: {new Date(tokenUsage.lastUpdated).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
